import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { createInvitation } from 'requests/invitation'

import { UserRole } from 'types/user'
import { AlertVariant } from 'types/alert'

import { addAlert } from 'store/alerts/alerts.actions'
import { selectCurrentUser, selectToken } from 'store/users/users.selectors'

interface UseCreatingInvitationProps {
  accountId?: number
}

interface UseCreatingInvitationReturn {
  email: string
  isAdmin: boolean
  handleEmailChange: (value: string) => void
  handleAdminChange: (value: boolean) => void
  handleSubmit: () => Promise<void>
  resetInvitation: () => void
  isLoading: boolean
  isValidEmail: boolean
  invalidEmailMessage: string
  invitationUrl: string | null
  copyInvitationUrl: () => void
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

export const useCreatingInvitation = ({ accountId }: UseCreatingInvitationProps): UseCreatingInvitationReturn => {
  const dispatch = useDispatch()
  const user = useSelector(selectCurrentUser)
  const token = useSelector(selectToken)
  const [email, setEmail] = useState<string>('')
  const [isAdmin, setIsAdmin] = useState<boolean>(user!.role === UserRole.ADMIN)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null)

  const isValidEmail = EMAIL_REGEX.test(email)
  const invalidEmailMessage = !email.trim() ? 'Заполните поле.' : 'Введите корректный email сотрудника.'

  const handleEmailChange = useCallback((value: string) => setEmail(value), [setEmail])
  const handleAdminChange = useCallback((value: boolean) => setIsAdmin(value), [setIsAdmin])

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await createInvitation({ email, accountId: accountId ? String(accountId) : undefined, isAdmin }, token!)

      setInvitationUrl(response.invitationUrl)
      dispatch(
        addAlert({
          header: 'Отправка приглашения',
          lifetime: 3000,
          variant: AlertVariant.SUCCESS,
          text: `Приглашение успешно отправлено`,
        }),
      )
    } catch (error) {
      dispatch(
        addAlert({
          header: 'Отправка приглашения',
          lifetime: 3000,
          variant: AlertVariant.DANGER,
          text: 'Ошибка отправки приглашения',
        }),
      )
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, email, accountId, isAdmin, token])

  const resetInvitation = useCallback(() => setInvitationUrl(null), [setInvitationUrl])

  const copyInvitationUrl = useCallback(() => {
    if (invitationUrl) {
      navigator.clipboard.writeText(invitationUrl)
      dispatch(
        addAlert({
          text: `Ссылка успешно скопирована`,
          lifetime: 3000,
          variant: AlertVariant.SUCCESS,
        }),
      )
    }
  }, [dispatch, invitationUrl])

  return {
    email,
    isAdmin,
    handleEmailChange,
    handleAdminChange,
    handleSubmit,
    isLoading,
    isValidEmail,
    invalidEmailMessage,
    invitationUrl,
    resetInvitation,
    copyInvitationUrl,
  }
}
